import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import ReactDOM from 'react-dom';
import { 
  CheckCircle, 
  Truck, 
  MapPin, 
  Navigation, 
  Flag,
  Upload,
  FileText,
  Camera,
  Loader2,
  X,
  UserPlus,
  ArrowLeft,
  Package,
  Search,
  Star,
  AlertTriangle,
  Info,
  ChevronRight,
  Weight,
  Ruler,
  ChevronDown,
  Menu,
  Smartphone,
  Radio,
  Wifi,
  WifiOff,
  Gauge,
  Fuel,
  Clock
} from 'lucide-react';
import api, { samsaraApi, loadTrackingApi, exceptionApi } from '../utils/api';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import { updateCarrierLoad } from '../store/slices/loadCarrierSlice';
import { updateLoad } from '../store/slices/loadSlice';
import { getLoadDisplayId } from '../utils/loadUtils';

// Hook to detect mobile screen size
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Mobile-optimized header component
const MobileHeader = ({ title, subtitle, onBack, onClose, actions = [] }) => (
  <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3 md:px-6 md:py-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 -ml-2 rounded-xl hover:bg-gray-100 transition-colors touch-manipulation"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-gray-900 truncate">
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm text-gray-500 truncate">{subtitle}</p>
          )}
        </div>
      </div>
      
      {/* Mobile action menu or desktop actions */}
      <div className="flex items-center space-x-2">
        {actions.length > 0 && (
          <div className="hidden md:flex space-x-2">
            {actions}
          </div>
        )}
        
        {/* Mobile menu for actions */}
        {actions.length > 0 && (
          <div className="md:hidden">
            <MobileActionMenu actions={actions} />
          </div>
        )}
        
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors touch-manipulation"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>
    </div>
  </div>
);

// Mobile action menu component
const MobileActionMenu = ({ actions }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-xl hover:bg-gray-100 transition-colors touch-manipulation"
      >
        <Menu className="w-5 h-5 text-gray-600" />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-40 bg-black/20" 
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 z-50"
            >
              <div className="py-2">
                {actions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      action.props.onClick();
                      setIsOpen(false);
                    }}
                    disabled={action.props.disabled}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-3 touch-manipulation"
                  >
                    {action.props.children}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// Enhanced Driver Selection View with mobile optimizations
const DriverSelectView = ({ 
  availableDrivers, 
  selectedDriver, 
  setSelectedDriver, 
  onSelect, 
  onBack, 
  onClose,
  loading,
  currentDriverId 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useIsMobile();

  const filteredDrivers = availableDrivers.filter(driver => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      driver.name?.toLowerCase().includes(search) ||
      driver.license_number?.toLowerCase().includes(search) ||
      driver.phone?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="h-full flex flex-col">
      <MobileHeader 
        title="Select Driver"
        subtitle="Choose a driver for this load"
        onBack={onBack}
        onClose={onClose}
      />

      {/* Mobile-optimized search */}
      <div className="px-4 py-4 border-b border-gray-100 bg-gray-50/50 md:px-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search drivers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 pr-4 py-4 w-full border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base touch-manipulation"
          />
        </div>
      </div>

      {/* Driver List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : filteredDrivers.length === 0 ? (
          <div className="text-center py-12 px-4">
            <UserPlus className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No drivers match your search' : 'No drivers available'}
            </h4>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search terms' : 'Add drivers to enable assignment'}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-3 md:p-6">
            {filteredDrivers.map((driver) => {
              const isCurrentlyAssigned = driver.id === currentDriverId;
              
              return (
                <motion.div
                  key={driver.id}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onSelect(driver.id)}
                  className={`
                    relative p-4 md:p-6 rounded-2xl border-2 cursor-pointer transition-all touch-manipulation
                    ${isCurrentlyAssigned 
                      ? 'border-blue-300 bg-blue-50 shadow-lg shadow-blue-100' 
                      : selectedDriver === driver.id 
                        ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-100' 
                        : 'border-gray-200 hover:border-blue-200 hover:shadow-md active:shadow-lg'
                    }
                  `}
                >
                  {/* Mobile-optimized header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-lg shadow-lg flex-shrink-0">
                        {driver.name?.charAt(0)?.toUpperCase() || 'D'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-gray-900 text-lg truncate">{driver.name}</h4>
                        <p className="text-sm text-gray-600 font-medium">License: {driver.license_number}</p>
                        {driver.phone && (
                          <p className="text-sm text-gray-500">{driver.phone}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Status badges - stacked on mobile */}
                    <div className="flex flex-col items-end space-y-2 flex-shrink-0">
                      {isCurrentlyAssigned && (
                        <div className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-bold shadow-lg">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          CURRENT
                        </div>
                      )}
                      
                      {selectedDriver === driver.id && !isCurrentlyAssigned && (
                        <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      )}
                    </div>
                  </div>

                  {/* Action indicator for currently assigned */}
                  {isCurrentlyAssigned && (
                    <div className="mt-4 flex items-center justify-center py-2 text-blue-600 bg-blue-100 rounded-xl">
                      <span className="text-sm font-medium">Currently assigned to this load</span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced Vehicle Selection View with mobile optimizations
const VehicleSelectView = ({ 
  vehicles, 
  selectedVehicle, 
  setSelectedVehicle, 
  onSelect, 
  onSelectWithTrailer,
  onBack, 
  onClose,
  loading,
  currentVehicleId 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const isMobile = useIsMobile();

  const filteredVehicles = vehicles.filter(vehicle => {
    if (!vehicle || typeof vehicle !== 'object') return false;
    
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const vehicleMatch = [
        vehicle.plate_number,
        vehicle.make,
        vehicle.model,
        vehicle.tractor_number,
        vehicle.type
      ].some(field => field?.toLowerCase().includes(searchLower));

      const trailerMatch = vehicle.trailers?.some(trailer => [
        trailer.trailer_number,
        trailer.type,
        trailer.make,
        trailer.model
      ].some(field => field?.toLowerCase().includes(searchLower)));

      if (!vehicleMatch && !trailerMatch) return false;
    }
    
    // Status filter
    if (filterType === 'available') {
      return vehicle.status === 'available';
    } else if (filterType === 'in_use') {
      return vehicle.status === 'in_use';
    }
    
    return true;
  });

  const getStatusConfig = (status) => {
    switch (status) {
      case 'available':
        return { color: 'text-green-700 bg-green-100', label: 'Available' };
      case 'in_use':
        return { color: 'text-blue-700 bg-blue-100', label: 'In Use' };
      case 'maintenance':
        return { color: 'text-amber-700 bg-amber-100', label: 'Maintenance' };
      default:
        return { color: 'text-gray-700 bg-gray-100', label: status || 'Unknown' };
    }
  };

  return (
    <div className="h-full flex flex-col">
      <MobileHeader 
        title="Select Vehicle"
        subtitle="Choose a vehicle for this load"
        onBack={onBack}
        onClose={onClose}
      />

      {/* Mobile-optimized search and filters */}
      <div className="px-4 py-4 border-b border-gray-100 bg-gray-50/50 space-y-4 md:px-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search vehicles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 pr-4 py-4 w-full border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base touch-manipulation"
          />
        </div>
        
        {/* Mobile-friendly filter tabs */}
        <div className="flex bg-white rounded-2xl border border-gray-200 p-1">
          {[
            { key: 'all', label: 'All' },
            { key: 'available', label: 'Available' },
            { key: 'in_use', label: 'In Use' }
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setFilterType(filter.key)}
              className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all touch-manipulation ${
                filterType === filter.key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Vehicle List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No vehicles match your search' : 'No vehicles available'}
            </h4>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search terms' : 'Add vehicles to enable assignment'}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-4 md:p-6">
            {filteredVehicles.map((vehicle) => {
              const isCurrentlyAssigned = vehicle.id === currentVehicleId;
              const isInUse = vehicle.status === 'in_use' && !isCurrentlyAssigned;
              const isAvailableForSelection = vehicle.status === 'available' || isCurrentlyAssigned;
              const statusConfig = getStatusConfig(vehicle.status);
              const trailerCount = vehicle.trailer_count || 0;
              
              return (
                <motion.div
                  key={vehicle.id}
                  whileTap={isAvailableForSelection ? { scale: 0.98 } : {}}
                  onClick={isAvailableForSelection ? () => onSelect(vehicle.id) : undefined}
                  className={`
                    relative p-4 md:p-6 rounded-2xl border-2 transition-all
                    ${isInUse 
                      ? 'border-gray-300 bg-gray-50 opacity-60 blur-[0.5px] cursor-not-allowed pointer-events-none' 
                      : isCurrentlyAssigned 
                        ? 'border-blue-300 bg-blue-50 cursor-pointer' 
                        : 'border-gray-200 hover:border-blue-200 hover:shadow-md cursor-pointer'
                    }
                  `}
                >
                  {/* Mobile-optimized header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg flex-shrink-0">
                        <Truck className="w-6 h-6" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-lg font-bold text-gray-900 truncate">
                          {vehicle.year} {vehicle.type}
                        </h4>
                        {vehicle.make && vehicle.model && (
                          <p className="text-sm text-gray-600 font-medium truncate">
                            {vehicle.make} {vehicle.model}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Status badges - stacked on mobile */}
                    <div className="flex flex-col items-end space-y-2 flex-shrink-0">
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-full ${statusConfig.color}`}>
                        <span className="text-xs font-medium">{statusConfig.label}</span>
                      </div>
                      
                      {isCurrentlyAssigned && (
                        <div className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-bold shadow-lg">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          CURRENT
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Vehicle details - simplified for mobile */}
                  {(vehicle.tractor_number || vehicle.plate_number) && (
                    <div className="flex flex-wrap gap-4 mb-4 text-xs text-gray-500">
                      {vehicle.tractor_number && (
                        <span><strong>Tractor:</strong> {vehicle.tractor_number}</span>
                      )}
                      {vehicle.plate_number && (
                        <span><strong>Plate:</strong> {vehicle.plate_number}</span>
                      )}
                    </div>
                  )}

                  {/* Trailer info - collapsible on mobile */}
                  {trailerCount > 0 && (
                    <div className="bg-gray-50 rounded-xl p-3 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Package className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-gray-900 text-sm">
                            {trailerCount} Trailer{trailerCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {vehicle.primary_trailer && (
                          <span className="text-xs text-blue-600 font-medium">
                            Primary: {vehicle.primary_trailer.trailer_number}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action buttons - full width on mobile */}
                  <div className="flex flex-col md:flex-row gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isAvailableForSelection) {
                          onSelect(vehicle.id);
                        }
                      }}
                      disabled={isCurrentlyAssigned || isInUse}
                      className={`
                        flex-1 py-4 px-4 rounded-xl font-medium text-sm transition-all touch-manipulation
                        ${isInUse
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : isCurrentlyAssigned
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                        }
                      `}
                    >
                      {isInUse 
                        ? 'Vehicle In Use' 
                        : isCurrentlyAssigned 
                          ? 'Currently Selected' 
                          : 'Select Vehicle Only'
                      }
                    </button>
                    
                    {trailerCount > 0 && !isCurrentlyAssigned && !isInUse && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectWithTrailer(vehicle);
                        }}
                        className="md:flex-none py-4 px-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-medium text-sm shadow-lg hover:shadow-xl transition-all touch-manipulation flex items-center justify-center"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Select w/ Trailer
                      </button>
                    )}
                  </div>
                  
                  {/* In-use overlay for visual emphasis */}
                  {isInUse && (
                    <div className="absolute inset-0 bg-gray-100/50 rounded-2xl flex items-center justify-center pointer-events-none">
                      <div className="bg-white px-4 py-2 rounded-xl shadow-lg border border-gray-200">
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Truck className="w-4 h-4" />
                          <span className="text-sm font-medium">Currently In Use</span>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced Trailer Selection View with mobile optimizations
const TrailerSelectView = ({ 
  vehicleId,
  trailers,
  selectedTrailer,
  setSelectedTrailer,
  onSelect,
  onBack,
  onClose,
  loading,
  currentTrailerId
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const isMobile = useIsMobile();

  const filteredTrailers = trailers.filter(trailer => {
    if (!trailer || typeof trailer !== 'object') return false;
    
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = [
        trailer.trailer_number,
        trailer.type,
        trailer.make,
        trailer.model,
        trailer.vin
      ].some(field => field?.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }
    
    // Status filter
    if (filterType === 'available') {
      return trailer.is_available === true;
    } else if (filterType === 'assigned') {
      return trailer.is_available === false;
    }
    
    return true;
  });

  const getStatusConfig = (status) => {
    switch (status) {
      case 'available':
        return { color: 'text-green-700 bg-green-100 border-green-200', icon: CheckCircle, label: 'Available' };
      case 'in_use':
        return { color: 'text-blue-700 bg-blue-100 border-blue-200', icon: Truck, label: 'In Use' };
      case 'maintenance':
        return { color: 'text-amber-700 bg-amber-100 border-amber-200', icon: AlertTriangle, label: 'Maintenance' };
      default:
        return { color: 'text-gray-700 bg-gray-100 border-gray-200', icon: Info, label: status || 'Unknown' };
    }
  };

  return (
    <div className="h-full flex flex-col">
      <MobileHeader 
        title="Select Trailer"
        subtitle="Choose a trailer for this vehicle"
        onBack={onBack}
        onClose={onClose}
      />

      {/* Mobile-optimized search and filters */}
      <div className="px-4 py-4 border-b border-gray-100 bg-gray-50/50 space-y-4 md:px-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search trailers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 pr-4 py-4 w-full border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base touch-manipulation"
          />
        </div>
        
        <div className="flex bg-white rounded-2xl border border-gray-200 p-1">
          {[
            { key: 'all', label: 'All' },
            { key: 'available', label: 'Available' },
            { key: 'assigned', label: 'In Use' }
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setFilterType(filter.key)}
              className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all touch-manipulation ${
                filterType === filter.key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Trailer List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : filteredTrailers.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No trailers match your search' : 'No trailers available'}
            </h4>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search terms' : 'Add trailers to enable assignment'}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-4 md:p-6">
            {filteredTrailers.map((trailer) => {
              const isCurrentlyAssigned = trailer.id === currentTrailerId;
              const isInUse = !trailer.is_available && !isCurrentlyAssigned;
              const isAvailableForAssignment = trailer.is_available || isCurrentlyAssigned;
              const statusConfig = getStatusConfig(trailer.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <motion.div
                  key={trailer.id}
                  whileTap={isAvailableForAssignment ? { scale: 0.98 } : {}}
                  onClick={() => isAvailableForAssignment && onSelect(trailer.id, trailer)}
                  className={`
                    relative p-4 md:p-6 rounded-2xl border-2 transition-all
                    ${isInUse
                      ? 'border-gray-300 bg-gray-50 opacity-60 blur-[0.5px] cursor-not-allowed pointer-events-none'
                      : isCurrentlyAssigned 
                        ? 'border-blue-300 bg-blue-50 cursor-pointer' 
                        : isAvailableForAssignment 
                          ? 'border-gray-200 bg-white hover:border-blue-200 hover:shadow-md cursor-pointer' 
                          : 'border-gray-300 bg-gray-50 opacity-60 cursor-not-allowed'
                    }
                  `}
                >
                  {/* Mobile-optimized header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg flex-shrink-0">
                        <Package className="w-6 h-6" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-xl font-bold text-gray-900 truncate">
                          {trailer.trailer_number}
                        </h4>
                        {trailer.type && (
                          <p className="text-sm text-gray-600 font-medium">{trailer.type}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Status badges - stacked on mobile */}
                    <div className="flex flex-col items-end space-y-2 flex-shrink-0">
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-full border ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        <span className="text-xs font-medium">{statusConfig.label}</span>
                      </div>
                      
                      {trailer.is_primary && (
                        <div className="flex items-center px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full text-xs font-bold shadow-lg">
                          <Star className="w-3 h-3 mr-1" />
                          PRIMARY
                        </div>
                      )}
                      
                      {isCurrentlyAssigned && (
                        <div className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-bold shadow-lg">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          CURRENT
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Simplified details for mobile */}
                  {(trailer.make || trailer.model || trailer.year) && (
                    <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                      {trailer.make && trailer.model && (
                        <div>
                          <p className="text-gray-500 text-xs">Make/Model</p>
                          <p className="font-medium text-gray-900">{trailer.make} {trailer.model}</p>
                        </div>
                      )}
                      {trailer.year && (
                        <div>
                          <p className="text-gray-500 text-xs">Year</p>
                          <p className="font-medium text-gray-900">{trailer.year}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action indicator */}
                  {!isCurrentlyAssigned && !isInUse && isAvailableForAssignment && (
                    <div className="mt-4 flex items-center justify-center py-2 text-blue-600 bg-blue-50 rounded-xl">
                      <span className="text-sm font-medium mr-2">Tap to select this trailer</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  )}
                  
                  {isInUse && (
                    <div className="mt-4 flex items-center justify-center py-2 text-gray-400 bg-gray-100 rounded-xl">
                      <span className="text-sm font-medium">Trailer currently in use</span>
                    </div>
                  )}
                  
                  {!isAvailableForAssignment && !isInUse && (
                    <div className="mt-4 flex items-center justify-center py-2 text-gray-400 bg-gray-100 rounded-xl">
                      <span className="text-sm font-medium">Not available for assignment</span>
                    </div>
                  )}
                  
                  {/* In-use overlay for visual emphasis */}
                  {isInUse && (
                    <div className="absolute inset-0 bg-gray-100/50 rounded-2xl flex items-center justify-center pointer-events-none">
                      <div className="bg-white px-4 py-2 rounded-xl shadow-lg border border-gray-200">
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Package className="w-4 h-4" />
                          <span className="text-sm font-medium">Currently In Use</span>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Tracking Source Selection View - Choose between Driver App and Samsara ELD
const TrackingSourceSelectView = ({
  samsaraConnected,
  selectedTrackingSource,
  onSelect,
  onBack,
  onClose,
  loading
}) => {
  const isMobile = useIsMobile();

  const trackingOptions = [
    {
      id: 'driver_app',
      title: 'Load Link Driver App',
      description: 'Track via our mobile driver application with real-time GPS updates',
      icon: Smartphone,
      available: true,
      features: ['Real-time GPS tracking', 'Status updates from driver', 'Photo documentation', 'In-app messaging'],
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      id: 'samsara',
      title: 'Samsara ELD',
      description: samsaraConnected 
        ? 'Track via your connected Samsara fleet management system'
        : 'Connect Samsara in Settings to enable ELD tracking',
      icon: Radio,
      available: samsaraConnected,
      features: ['ELD-based tracking', 'Engine diagnostics', 'Fuel monitoring', 'Hours of service'],
      gradient: 'from-emerald-500 to-teal-600',
      bgGradient: 'from-emerald-50 to-teal-50',
      borderColor: 'border-emerald-200',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600'
    }
  ];

  return (
    <div className="h-full flex flex-col">
      <MobileHeader 
        title="Select Tracking Method"
        subtitle="Choose how to track this load"
        onBack={onBack}
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {/* Info banner */}
        <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Tracking Source Selection</p>
              <p className="text-sm text-amber-700 mt-1">
                Choose how you want to track this load. You can change this later from the Load Tracking page.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {trackingOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedTrackingSource === option.id;
            const isDisabled = !option.available;

            return (
              <motion.button
                key={option.id}
                onClick={() => !isDisabled && onSelect(option.id)}
                disabled={isDisabled || loading}
                whileTap={!isDisabled ? { scale: 0.98 } : {}}
                className={`
                  w-full text-left p-5 md:p-6 rounded-2xl border-2 transition-all duration-200 touch-manipulation
                  ${isDisabled 
                    ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200' 
                    : isSelected 
                      ? `bg-gradient-to-br ${option.bgGradient} ${option.borderColor} shadow-lg`
                      : `bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300 hover:shadow-md`
                  }
                `}
              >
                <div className="flex items-start space-x-4">
                  {/* Icon */}
                  <div className={`
                    p-3 rounded-xl flex-shrink-0
                    ${isSelected 
                      ? `bg-gradient-to-br ${option.gradient} text-white shadow-lg` 
                      : `${option.iconBg} ${option.iconColor}`
                    }
                  `}>
                    <Icon className="w-6 h-6" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className={`text-lg font-semibold ${isSelected ? 'text-gray-900' : 'text-gray-800'}`}>
                        {option.title}
                      </h4>
                      {isSelected && (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      )}
                      {isDisabled && (
                        <WifiOff className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                    <p className={`text-sm ${isSelected ? 'text-gray-700' : 'text-gray-500'} mb-3`}>
                      {option.description}
                    </p>

                    {/* Features */}
                    {option.available && (
                      <div className="flex flex-wrap gap-2">
                        {option.features.slice(0, isMobile ? 2 : 4).map((feature, idx) => (
                          <span 
                            key={idx}
                            className={`
                              text-xs px-2 py-1 rounded-full
                              ${isSelected 
                                ? 'bg-white/70 text-gray-700' 
                                : 'bg-gray-100 text-gray-600'
                              }
                            `}
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Not connected message */}
                    {isDisabled && (
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <AlertTriangle className="w-4 h-4 mr-1 text-amber-500" />
                        Connect Samsara in Settings to enable
                      </div>
                    )}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto border-t border-gray-100 bg-white">
        <div className="px-4 py-4 md:px-6 md:pb-4" style={{ paddingBottom: isMobile ? 'max(1rem, env(safe-area-inset-bottom))' : undefined }}>
          <div className="flex flex-col md:flex-row gap-3 md:justify-between md:items-center">
            <button
              onClick={onBack}
              className="order-2 md:order-1 px-6 py-4 text-gray-600 hover:text-gray-800 transition-colors touch-manipulation rounded-2xl hover:bg-gray-50"
            >
              Back
            </button>
            <div className="order-1 md:order-2 text-center md:text-right">
              <p className="text-sm text-gray-500">
                {selectedTrackingSource === 'driver_app' && 'Continue to select a vehicle'}
                {selectedTrackingSource === 'samsara' && 'Continue to select a Samsara vehicle'}
                {!selectedTrackingSource && 'Select a tracking method to continue'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Samsara Vehicle Selection View
const SamsaraVehicleSelectView = ({
  vehicles,
  selectedVehicle,
  onSelect,
  onBack,
  onClose,
  loading,
  loadingSamsaraVehicles
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useIsMobile();

  const filteredVehicles = vehicles.filter(vehicle => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      vehicle.name?.toLowerCase().includes(search) ||
      vehicle.serial?.toLowerCase().includes(search) ||
      vehicle.vin?.toLowerCase().includes(search) ||
      vehicle.licensePlate?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="h-full flex flex-col">
      <MobileHeader 
        title="Select Samsara Vehicle"
        subtitle="Choose a vehicle from your Samsara fleet"
        onBack={onBack}
        onClose={onClose}
      />

      {/* Search */}
      <div className="px-4 py-4 border-b border-gray-100 bg-gray-50/50 md:px-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search vehicles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 pr-4 py-4 w-full border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-base touch-manipulation"
          />
        </div>
      </div>

      {/* Samsara connection indicator */}
      <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100 md:px-6">
        <div className="flex items-center space-x-2">
          <Wifi className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-medium text-emerald-700">Connected to Samsara</span>
          <span className="text-sm text-emerald-600">• {vehicles.length} vehicles available</span>
        </div>
      </div>

      {/* Vehicle List */}
      <div className="flex-1 overflow-y-auto">
        {loadingSamsaraVehicles ? (
          <div className="flex items-center justify-center h-40">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">Loading Samsara vehicles...</p>
            </div>
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Radio className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No vehicles match your search' : 'No Samsara vehicles found'}
            </h4>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search terms' : 'Make sure vehicles are configured in your Samsara account'}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-3 md:p-6">
            {filteredVehicles.map((vehicle) => {
              const isSelected = selectedVehicle?.id === vehicle.id;
              
              return (
                <motion.div
                  key={vehicle.id}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onSelect(vehicle)}
                  className={`
                    relative p-4 md:p-5 rounded-2xl border-2 cursor-pointer transition-all touch-manipulation
                    ${isSelected 
                      ? 'border-emerald-400 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-lg shadow-emerald-100' 
                      : 'border-gray-200 bg-white hover:border-emerald-200 hover:shadow-md'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      {/* Vehicle Icon */}
                      <div className={`
                        p-3 rounded-xl flex-shrink-0
                        ${isSelected 
                          ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg' 
                          : 'bg-emerald-100 text-emerald-600'
                        }
                      `}>
                        <Truck className="w-6 h-6" />
                      </div>
                      
                      {/* Vehicle Info */}
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-gray-900 text-lg truncate">
                          {vehicle.name}
                        </h4>
                        {vehicle.serial && (
                          <p className="text-sm text-gray-600">Serial: {vehicle.serial}</p>
                        )}
                        {vehicle.licensePlate && (
                          <p className="text-sm text-gray-500">Plate: {vehicle.licensePlate}</p>
                        )}
                      </div>
                    </div>

                    {/* Selection indicator */}
                    {isSelected && (
                      <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                    )}
                  </div>

                  {/* Vehicle stats if available */}
                  {(vehicle.engineState || vehicle.fuelPercent !== undefined) && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-3">
                      {vehicle.engineState && (
                        <div className="flex items-center text-sm">
                          <Gauge className="w-4 h-4 mr-1 text-gray-400" />
                          <span className={`
                            ${vehicle.engineState === 'On' ? 'text-green-600' : 'text-gray-500'}
                          `}>
                            Engine {vehicle.engineState}
                          </span>
                        </div>
                      )}
                      {vehicle.fuelPercent !== undefined && (
                        <div className="flex items-center text-sm">
                          <Fuel className="w-4 h-4 mr-1 text-gray-400" />
                          <span className="text-gray-600">{vehicle.fuelPercent}% fuel</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Select action indicator */}
                  {isSelected && (
                    <div className="mt-3 flex items-center justify-center py-2 text-emerald-600 bg-emerald-100 rounded-xl">
                      <span className="text-sm font-medium">Selected for tracking</span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-auto border-t border-gray-100 bg-white">
        <div className="px-4 py-4 md:px-6 md:pb-4" style={{ paddingBottom: isMobile ? 'max(1rem, env(safe-area-inset-bottom))' : undefined }}>
          <div className="flex flex-col md:flex-row gap-3 md:justify-between md:items-center">
            <button
              onClick={onBack}
              disabled={loading}
              className="order-2 md:order-1 px-6 py-4 text-gray-600 hover:text-gray-800 transition-colors touch-manipulation rounded-2xl hover:bg-gray-50"
            >
              Back
            </button>
            {selectedVehicle && (
              <div className="order-1 md:order-2 flex items-center justify-center text-sm text-emerald-600">
                <CheckCircle className="w-4 h-4 mr-2" />
                <span>Tap selected vehicle to confirm</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PodTypeSelector = ({ podType, setPodType }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {[
      {
        type: 'soft',
        icon: FileText,
        title: 'Soft POD',
        description: 'Digital signature confirmation'
      },
      {
        type: 'hard',
        icon: Camera,
        title: 'Hard POD',
        description: 'Upload document image'
      }
    ].map(option => (
      <button
        key={option.type}
        type="button"
        onClick={() => setPodType(option.type)}
        className={`
          p-6 rounded-2xl border-2 transition-all duration-200 touch-manipulation
          hover:shadow-sm hover:border-blue-200 active:scale-98
          ${podType === option.type 
            ? 'border-blue-500 bg-blue-50/50 shadow-sm shadow-blue-100' 
            : 'border-gray-200'
          }
        `}
      >
        <option.icon className="w-8 h-8 mb-3 text-blue-600 mx-auto" />
        <div className="font-medium text-gray-900 text-lg mb-1">{option.title}</div>
        <div className="text-sm text-gray-500">{option.description}</div>
      </button>
    ))}
  </div>
);

const DocumentPreview = ({ files, removeFile }) => (
  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
    {files.map((file, index) => {
      const isImage = file.type.startsWith('image/');
      const preview = isImage ? URL.createObjectURL(file) : null;
      
      return (
        <div 
          key={index} 
          className="relative group bg-gray-50 rounded-2xl p-4 border border-gray-200"
        >
          {isImage ? (
            <div className="relative aspect-[3/2] w-full overflow-hidden rounded-xl bg-gray-100">
              <img
                src={preview}
                alt={`Preview ${index + 1}`}
                className="h-full w-full object-cover"
                onLoad={() => URL.revokeObjectURL(preview)}
              />
            </div>
          ) : (
            <div className="relative aspect-[3/2] w-full overflow-hidden rounded-xl bg-gray-100 flex items-center justify-center">
              <FileText className="w-12 h-12 text-gray-400" />
            </div>
          )}
          
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600 truncate flex-1 font-medium">
              Page {index + 1}
            </span>
            <button
              onClick={() => removeFile(index)}
              className="p-2 rounded-full hover:bg-gray-200 text-gray-500 hover:text-red-500 transition-colors touch-manipulation"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      );
    })}
  </div>
);

const PodView = ({
  podType,
  setPodType,
  signedBy,
  setSignedBy,
  podNotes,
  setPodNotes,
  podFiles,
  setPodFiles,
  loading,
  handlePodSubmit,
  onBack,
  onClose,
}) => {
  const fileInputRef = React.useRef(null);
  const isMobile = useIsMobile();

  const onDrop = useCallback((acceptedFiles) => {
    setPodFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, [setPodFiles]);

  const removeFile = useCallback((index) => {
    setPodFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  }, [setPodFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png'],
      'application/pdf': ['.pdf']
    },
    multiple: true,
    noClick: true,
    noKeyboard: true
  });

  const handleAddMorePages = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const canSubmit = podType && 
    ((podType === 'soft' && signedBy.trim()) || 
     (podType === 'hard' && podFiles.length > 0));

  return (
    <div className="h-full flex flex-col">
      <MobileHeader 
        title="Proof of Delivery"
        subtitle="Complete delivery confirmation"
        onBack={onBack}
        onClose={onClose}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 md:p-6 space-y-6">
          {/* POD Type Selection */}
          <div>
            <label className="block text-lg font-semibold text-gray-900 mb-4">
              Select Proof of Delivery Type
            </label>
            <PodTypeSelector podType={podType} setPodType={setPodType} />
          </div>

          {/* Soft POD Form */}
          {podType === 'soft' && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div>
                <label className="block text-base font-medium text-gray-700 mb-3">
                  Signed By <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={signedBy}
                  onChange={(e) => setSignedBy(e.target.value)}
                  className="block w-full px-4 py-4 rounded-2xl border border-gray-200 
                    shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 
                    transition-all duration-200 text-base touch-manipulation"
                  placeholder="Enter name of person who signed for delivery"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-700 mb-3">
                  Additional Notes
                </label>
                <textarea
                  value={podNotes}
                  onChange={(e) => setPodNotes(e.target.value)}
                  className="block w-full px-4 py-4 rounded-2xl border border-gray-200 
                    shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 
                    transition-all duration-200 text-base touch-manipulation resize-none"
                  rows={isMobile ? 3 : 4}
                  placeholder="Add any additional notes about the delivery..."
                />
              </div>
            </motion.div>
          )}

          {/* Hard POD Upload */}
          {podType === 'hard' && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <label className="block text-base font-medium text-gray-700 mb-3">
                Upload POD Document <span className="text-red-500">*</span>
              </label>
              
              {podFiles.length === 0 ? (
                <div
                  {...getRootProps()}
                  className={`
                    border-2 border-dashed rounded-2xl p-8 md:p-12 text-center transition-all duration-200
                    ${isDragActive 
                      ? 'border-blue-400 bg-blue-50 shadow-sm' 
                      : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="space-y-4">
                    <Upload className="mx-auto h-16 w-16 md:h-20 md:w-20 text-gray-400" />
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={handleAddMorePages}
                        className="text-xl font-semibold text-blue-600 hover:text-blue-700 
                          focus:outline-none focus:underline transition-colors touch-manipulation"
                      >
                        Tap to upload files
                      </button>
                      <p className="text-gray-600 text-base">or drag and drop</p>
                      <p className="text-sm text-gray-500">
                        Supports PNG, JPG, PDF • Up to 10MB each • Multiple pages allowed
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <DocumentPreview files={podFiles} removeFile={removeFile} />
                  
                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={handleAddMorePages}
                      className="inline-flex items-center px-6 py-4 border border-gray-300 
                        shadow-sm text-base font-medium rounded-2xl text-gray-700 
                        bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 
                        focus:ring-offset-2 focus:ring-blue-500 transition-all touch-manipulation"
                    >
                      <Upload className="h-5 w-5 mr-3" />
                      Add More Pages
                    </button>
                  </div>
                </div>
              )}
              
              <input
                {...getInputProps()}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
            </motion.div>
          )}
        </div>
      </div>

                       {/* Footer with safe area padding */}
                 <div className="mt-auto border-t border-gray-100 bg-white">
                     <div className="px-4 py-4 md:px-6 md:pb-4" style={{ paddingBottom: isMobile ? 'max(1rem, env(safe-area-inset-bottom))' : undefined }}>
          <div className="flex flex-col md:flex-row gap-3 md:justify-between md:items-center">
            <button
              onClick={onBack}
              className="order-2 md:order-1 px-6 py-4 text-gray-600 hover:text-gray-800 transition-colors touch-manipulation rounded-2xl hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handlePodSubmit}
              disabled={loading || !canSubmit}
              className={`
                order-1 md:order-2 px-8 py-4 rounded-2xl font-semibold text-base
                inline-flex items-center justify-center transition-all duration-200 touch-manipulation
                ${loading || !canSubmit
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl active:scale-98'
                }
              `}
            >
              {loading && <Loader2 className="w-5 h-5 mr-3 animate-spin" />}
              {loading ? 'Submitting...' : 'Submit POD'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mobile-optimized Status Timeline Item
const StatusTimelineItem = ({ status, isCompleted, isNext, selectedDriver, setSelectedDriver, availableDrivers }) => {
  const Icon = status.icon;
  const isMobile = useIsMobile();
  
  return (
    <div className={`
      flex items-start group relative
      ${!isCompleted && !isNext ? 'opacity-50' : ''}
    `}>
      {/* Status indicator */}
      <div className="flex-shrink-0 relative">
        <div className={`
          rounded-full p-3 md:p-2.5 transition-all duration-200
          ${isCompleted ? 'bg-green-500 text-white ring-4 ring-green-50' :
            isNext ? 'bg-blue-500 text-white ring-4 ring-blue-50 shadow-lg shadow-blue-100' :
            'bg-gray-100 text-gray-400'
          }
        `}>
          <Icon size={isMobile ? 18 : 16} strokeWidth={2.5} />
        </div>
        
        {/* Vertical connector line */}
        <div className={`
          absolute left-1/2 top-full h-full w-0.5 -translate-x-1/2
          ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}
        `} />
      </div>
      
      {/* Status content */}
      <div className="ml-4 md:ml-4 flex-1 pb-8">
        <div className={`
          p-4 md:p-3 rounded-2xl md:rounded-lg transition-all duration-200
          ${isNext ? 'bg-blue-50 ring-1 ring-blue-100' : ''}
        `}>
          <h4 className={`font-semibold text-base md:font-medium md:text-sm transition-colors duration-200 ${
            isCompleted ? 'text-green-600' :
            isNext ? 'text-blue-600' :
            'text-gray-500'
          }`}>
            {status.label}
          </h4>
          <p className="text-sm text-gray-500 mt-1">{status.description}</p>
          
          {/* Driver selection for next status */}
          {isNext && status.requiresDriver && (
            <div className="mt-4 space-y-3">
              <label className="text-sm font-medium text-gray-700">
                Select Driver
              </label>
              <select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
                className="block w-full pl-4 pr-10 py-3 md:py-2 text-base md:text-sm border-gray-300 
                  focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 
                  rounded-2xl md:rounded-lg transition-shadow duration-200 touch-manipulation"
              >
                <option value="">Choose a driver</option>
                {availableDrivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.name} - {driver.license_number}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced context notes component with mobile optimization
const RequirementAlert = ({ type, currentStatus, load }) => {
  const getRequirementInfo = () => {
    switch (type) {
      case 'driver_assignment':
        return {
          title: 'Driver & Vehicle Required',
          description: 'To progress to "Driver Assigned", you need both a driver and vehicle assigned to this load.',
          missingItems: [
            !load.assigned_driver_id && 'Driver not assigned',
            !load.assigned_vehicle_id && 'Vehicle not assigned'
          ].filter(Boolean),
          icon: UserPlus,
          color: 'blue'
        };
      case 'trailer_requirement':
        return {
          title: 'Trailer Required for Pickup',
          description: 'To reach the pickup location, a trailer must be assigned to the vehicle.',
          missingItems: [
            !load.assigned_trailer_id && 'Trailer not assigned'
          ].filter(Boolean),
          icon: Package,
          color: 'amber'
        };
      default:
        return null;
    }
  };

  const info = getRequirementInfo();
  if (!info || info.missingItems.length === 0) return null;

  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800'
  };

  return (
    <div className={`p-4 md:p-4 rounded-2xl md:rounded-lg border ${colorClasses[info.color]} mb-4`}>
      <div className="flex items-start space-x-3">
        <info.icon className="w-6 h-6 md:w-5 md:h-5 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="font-semibold text-base md:font-medium md:text-sm mb-2 md:mb-1">{info.title}</h4>
          <p className="text-sm mb-3 md:mb-2">{info.description}</p>
          <ul className="text-sm space-y-1">
            {info.missingItems.map((item, index) => (
              <li key={index} className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 rounded-full bg-current" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// Exception type configurations
const EXCEPTION_TYPES = {
  delay: { label: 'General Delay', icon: Clock, color: 'amber', description: 'Traffic, waiting times, or other delays' },
  weather: { label: 'Weather Issue', icon: AlertTriangle, color: 'blue', description: 'Adverse weather conditions' },
  mechanical: { label: 'Mechanical Issue', icon: AlertTriangle, color: 'red', description: 'Vehicle or equipment breakdown' },
  documentation: { label: 'Documentation Issue', icon: FileText, color: 'purple', description: 'Missing or incorrect documents' },
  shipper_issue: { label: 'Shipper Issue', icon: AlertTriangle, color: 'orange', description: 'Delay at shipper location' },
  receiver_issue: { label: 'Receiver Issue', icon: AlertTriangle, color: 'orange', description: 'Delay at receiver location' },
  other: { label: 'Other', icon: AlertTriangle, color: 'gray', description: 'Other exception type' }
};

const SEVERITY_LEVELS = {
  low: { label: 'Low', bgColor: 'bg-blue-50', textColor: 'text-blue-700', borderColor: 'border-blue-200' },
  medium: { label: 'Medium', bgColor: 'bg-amber-50', textColor: 'text-amber-700', borderColor: 'border-amber-200' },
  high: { label: 'High', bgColor: 'bg-orange-50', textColor: 'text-orange-700', borderColor: 'border-orange-200' },
  critical: { label: 'Critical', bgColor: 'bg-red-50', textColor: 'text-red-700', borderColor: 'border-red-200' }
};

// Exception Report View Component
const ExceptionReportView = ({ load, onBack, onClose, loading, setLoading, dispatch }) => {
  const [step, setStep] = useState('type'); // 'type' | 'details'
  const [selectedType, setSelectedType] = useState('delay');
  const [selectedSeverity, setSelectedSeverity] = useState('medium');
  const [notes, setNotes] = useState('');
  const [selectedStopSequence, setSelectedStopSequence] = useState(null);
  const isMobile = useIsMobile();

  const sortedStops = useMemo(() => {
    return [...(load?.stops || [])].sort((a, b) => a.sequence - b.sequence);
  }, [load?.stops]);

  const handleSubmitException = async () => {
    if (!notes.trim()) {
      toast.error('Please provide details about the exception');
      return;
    }

    try {
      setLoading(true);
      
      const response = await exceptionApi.createException(load.id, {
        exception_type: selectedType,
        severity: selectedSeverity,
        notes: notes.trim(),
        stop_sequence: selectedStopSequence
      });

      if (response.data) {
        toast.success(
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <div>
              <p className="font-medium">Exception Reported</p>
              <p className="text-sm text-gray-600">
                {EXCEPTION_TYPES[selectedType]?.label} reported for Load {getLoadDisplayId(load)}
              </p>
            </div>
          </div>,
          { icon: false }
        );

        // Update store
        if (response.data.load) {
          dispatch(updateCarrierLoad(response.data.load));
          dispatch(updateLoad(response.data.load));
        }

        onClose();
      }
    } catch (error) {
      console.error('Error creating exception:', error);
      toast.error(error.response?.data?.error || 'Failed to create exception');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'type') {
    return (
      <div className="h-full flex flex-col">
        <MobileHeader
          title="Report Exception"
          subtitle={`Load ${getLoadDisplayId(load)}`}
          onBack={onBack}
          onClose={onClose}
        />

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <p className="text-sm text-gray-600 mb-4">
            What type of exception are you reporting?
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(EXCEPTION_TYPES).map(([key, config]) => {
              const Icon = config.icon;
              const isSelected = selectedType === key;
              
              return (
                <button
                  key={key}
                  onClick={() => setSelectedType(key)}
                  className={`
                    p-4 rounded-xl border-2 text-left transition-all
                    ${isSelected 
                      ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-amber-100' : 'bg-gray-100'}`}>
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-amber-600' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${isSelected ? 'text-amber-900' : 'text-gray-900'}`}>
                        {config.label}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{config.description}</p>
                    </div>
                    {isSelected && <CheckCircle className="w-5 h-5 text-amber-500" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-auto border-t border-gray-100 bg-white p-4 md:p-6">
          <button
            onClick={() => setStep('details')}
            className="w-full py-3 px-4 bg-amber-600 text-white rounded-xl font-medium
                     hover:bg-amber-700 transition-colors flex items-center justify-center gap-2"
          >
            Continue
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <MobileHeader
        title="Exception Details"
        subtitle={EXCEPTION_TYPES[selectedType]?.label}
        onBack={() => setStep('type')}
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
        {/* Severity Selection */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Severity Level</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(SEVERITY_LEVELS).map(([key, config]) => {
              const isSelected = selectedSeverity === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedSeverity(key)}
                  className={`
                    py-3 px-4 rounded-xl border-2 text-center transition-all
                    ${isSelected 
                      ? `${config.borderColor} ${config.bgColor} ring-2 ring-opacity-50` 
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <p className={`font-medium text-sm ${isSelected ? config.textColor : 'text-gray-700'}`}>
                    {config.label}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Related Stop */}
        {sortedStops.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Related Stop (Optional)</h4>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedStopSequence(null)}
                className={`py-2 px-4 rounded-lg border text-sm transition-all
                  ${selectedStopSequence === null
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }
                `}
              >
                General
              </button>
              {sortedStops.map((stop) => (
                <button
                  key={stop.sequence}
                  onClick={() => setSelectedStopSequence(stop.sequence)}
                  className={`py-2 px-4 rounded-lg border text-sm transition-all
                    ${selectedStopSequence === stop.sequence
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }
                  `}
                >
                  Stop {stop.sequence + 1}: {stop.city}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Exception Details <span className="text-red-500">*</span>
          </h4>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Describe the exception in detail..."
            rows={5}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl 
                     focus:ring-2 focus:ring-amber-500 focus:border-transparent
                     resize-none text-sm placeholder:text-gray-400"
          />
        </div>
      </div>

      <div className="mt-auto border-t border-gray-100 bg-white p-4 md:p-6">
        <div className="flex gap-3">
          <button
            onClick={() => setStep('type')}
            className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium
                     hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={handleSubmitException}
            disabled={loading || !notes.trim()}
            className="flex-1 py-3 px-4 bg-amber-600 text-white rounded-xl font-medium
                     hover:bg-amber-700 transition-colors flex items-center justify-center gap-2
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Reporting...
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4" />
                Report Exception
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal views enum
const MODAL_VIEWS = {
  STATUS_UPDATE: 'status_update',
  DRIVER_SELECT: 'driver_select',
  TRACKING_SOURCE_SELECT: 'tracking_source_select',
  VEHICLE_SELECT: 'vehicle_select',
  TRAILER_SELECT: 'trailer_select',
  EXCEPTION_REPORT: 'exception_report',
  SAMSARA_VEHICLE_SELECT: 'samsara_vehicle_select',
  POD_SUBMIT: 'pod_submit'
};

// Sliding transition variants with mobile optimizations
const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
};

// Mobile swipe detection hook
const useSwipeNavigation = (onSwipeLeft, onSwipeRight, enabled = true) => {
  const handlePan = useCallback((event, info) => {
    if (!enabled) return;
    
    const threshold = 100;
    const velocityThreshold = 300;
    
    if (Math.abs(info.offset.x) > threshold || Math.abs(info.velocity.x) > velocityThreshold) {
      if (info.offset.x > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (info.offset.x < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    }
  }, [onSwipeLeft, onSwipeRight, enabled]);

  return { onPan: handlePan };
};

const StatusUpdateModal = React.forwardRef(
  ({ isOpen, onClose, load, currentStatus, onStatusUpdate }, ref) => {
    const [loading, setLoading] = useState(false);
    const [availableDrivers, setAvailableDrivers] = useState([]);
    const [availableVehicles, setAvailableVehicles] = useState([]);
    const [availableTrailers, setAvailableTrailers] = useState([]);
    
    // Modal view management
    const [currentView, setCurrentView] = useState(MODAL_VIEWS.STATUS_UPDATE);
    const [slideDirection, setSlideDirection] = useState(0);
    
    // Selection states
    const [selectedDriver, setSelectedDriver] = useState('');
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedTrailer, setSelectedTrailer] = useState(null);
    
    // Samsara/Tracking states
    const [samsaraConnected, setSamsaraConnected] = useState(false);
    const [samsaraVehicles, setSamsaraVehicles] = useState([]);
    const [loadingSamsaraVehicles, setLoadingSamsaraVehicles] = useState(false);
    const [selectedTrackingSource, setSelectedTrackingSource] = useState(load?.tracking_source || 'driver_app');
    const [selectedSamsaraVehicle, setSelectedSamsaraVehicle] = useState(null);
    
    // POD states
    const [podType, setPodType] = useState(null);
    const [podFiles, setPodFiles] = useState([]);
    const [signedBy, setSignedBy] = useState('');
    const [podNotes, setPodNotes] = useState('');
    
    const [isReassigning, setIsReassigning] = useState(false);
    const [wizardActive, setWizardActive] = useState(false);
    const dispatch = useDispatch();
    const isMobile = useIsMobile();

    // Sort stops by sequence
    const sortedStops = useMemo(() => {
      return [...(load.stops || [])].sort((a, b) => a.sequence - b.sequence);
    }, [load.stops]);

    // Update how we parse the current status
    const parseCurrentStatus = useMemo(() => {
      // Convert to string and handle all edge cases
      const safeStatus = currentStatus ? String(currentStatus).toLowerCase() : 'unknown';
      
      if (safeStatus.startsWith('in_transit_')) {
        // Extract the sequence numbers from the status
        const [_, fromSeq, toSeq] = safeStatus.split('_');
        return {
          type: 'in_transit',
          fromSequence: parseInt(fromSeq),
          toSequence: parseInt(toSeq)
        };
      } else if (safeStatus.startsWith('at_pickup_') || safeStatus.startsWith('at_delivery_')) {
        const [type, stopType, sequence] = safeStatus.split('_');
        return {
          type: `at_${stopType}`,
          sequence: parseInt(sequence)
        };
      } else if (safeStatus.startsWith('at_stop_')) {
        const [_, __, sequence] = safeStatus.split('_');
        return {
          type: 'at_stop',
          sequence: parseInt(sequence)
        };
      }
      
      return {
        type: safeStatus,
        sequence: null
      };
    }, [currentStatus]);

    // Generate dynamic status sequence
    const statusSequence = useMemo(() => {
      const sequence = [
        {
          key: 'assigned',
          label: 'Assigned',
          icon: CheckCircle,
          description: 'Load has been assigned to your company',
          requiresDriver: false
        },
        {
          key: 'assigned_driver',
          label: 'Driver Assigned',
          icon: Truck,
          description: 'Driver has been assigned to the load',
          requiresDriver: true
        },
        {
          key: 'trip_started',
          label: 'Trip Started',
          icon: Navigation,
          description: 'Driver has started the trip to first pickup',
          requiresDriver: false
        }
      ];

      // Add statuses for each stop
      sortedStops.forEach((stop, index) => {
        const isPickup = stop.stop_type === 'pickup';
        const isLastStop = index === sortedStops.length - 1;
        
        // Add stop status
        sequence.push({
          key: `at_stop_${stop.sequence}`,
          label: `${isPickup ? 'At Pickup' : 'At Delivery'} - ${stop.city}`,
          icon: isPickup ? MapPin : (isLastStop ? Flag : Navigation),
          description: `Driver has arrived at ${isPickup ? 'pickup' : 'delivery'} location in ${stop.city}, ${stop.state}`,
          requiresDriver: false,
          stopId: stop.id,
          sequence: stop.sequence
        });

        // Add "In Transit" status after each stop except the last one
        if (!isLastStop) {
          const nextStop = sortedStops[index + 1];
          sequence.push({
            key: `in_transit_${stop.sequence}_${nextStop.sequence}`,
            label: `In Transit to ${nextStop.city}`,
            icon: Navigation,
            description: `Load is in transit to ${nextStop.city}, ${nextStop.state}`,
            requiresDriver: false,
            fromStopId: stop.id,
            toStopId: nextStop.id,
            fromSequence: stop.sequence,
            toSequence: nextStop.sequence
          });
        }
      });

      // Add completed status
      sequence.push({
        key: 'completed',
        label: 'Completed',
        icon: CheckCircle,
        description: 'Load has been successfully delivered',
        requiresDriver: false
      });

      return sequence;
    }, [sortedStops]);

    // Navigation helpers
    const navigateToView = (view, direction = 1) => {
      setSlideDirection(direction);
      setCurrentView(view);
    };

    const goBack = () => {
      setSlideDirection(-1);
      setCurrentView(MODAL_VIEWS.STATUS_UPDATE);
    };

    // Swipe navigation
    const { onPan } = useSwipeNavigation(
      null, // No swipe left action for now
      () => {
        if (currentView !== MODAL_VIEWS.STATUS_UPDATE) {
          goBack();
        }
      },
      isMobile && currentView !== MODAL_VIEWS.STATUS_UPDATE
    );

    // Data fetching functions
    const fetchAvailableDrivers = useCallback(async () => {
      try {
        const response = await api.get('/api/carrier/available-drivers');
        setAvailableDrivers(response.data);
      } catch (error) {
        console.error('Failed to fetch available drivers:', error);
        toast.error('Failed to load available drivers');
      }
    }, []);

    const fetchAvailableVehicles = useCallback(async () => {
      try {
        const response = await api.get('/api/carrier/available-vehicles');
        setAvailableVehicles(response.data);
      } catch (error) {
        console.error('Failed to fetch available vehicles:', error);
        toast.error('Failed to load available vehicles');
      }
    }, []);

    const fetchAvailableTrailers = useCallback(async (vehicleId) => {
      try {
        const response = await api.get(`/api/vehicles/${vehicleId}/available-trailers`);
        const availableTrailers = response.data.available_trailers || [];
        const assignedTrailers = response.data.assigned_trailers || [];
        setAvailableTrailers([...availableTrailers, ...assignedTrailers]);
      } catch (error) {
        console.error('Failed to fetch available trailers:', error);
        toast.error('Failed to load available trailers');
      }
    }, []);

    // Check Samsara connection status
    const checkSamsaraStatus = useCallback(async () => {
      try {
        const response = await samsaraApi.getStatus();
        const connected = response.data?.connected === true;
        setSamsaraConnected(connected);
        return connected;
      } catch (error) {
        console.error('Failed to check Samsara status:', error);
        setSamsaraConnected(false);
        return false;
      }
    }, []);

    // Fetch Samsara vehicles
    const fetchSamsaraVehicles = useCallback(async () => {
      try {
        setLoadingSamsaraVehicles(true);
        const response = await samsaraApi.getVehicles();
        const vehicles = response.data?.vehicles || [];
        setSamsaraVehicles(vehicles);
        return vehicles;
      } catch (error) {
        console.error('Failed to fetch Samsara vehicles:', error);
        setSamsaraVehicles([]);
        return [];
      } finally {
        setLoadingSamsaraVehicles(false);
      }
    }, []);

    // Load data when modal opens
    useEffect(() => {
      if (isOpen) {
        fetchAvailableDrivers();
        fetchAvailableVehicles();
        checkSamsaraStatus();
      }
    }, [isOpen, fetchAvailableDrivers, fetchAvailableVehicles, checkSamsaraStatus]);

    // Find current status index and next status (REVERT to original logic)
    const currentStatusIndex = useMemo(() => {
      const index = statusSequence.findIndex(status => status.key === currentStatus);
      return index;
    }, [statusSequence, currentStatus]);
    
    const nextStatus = useMemo(() => {
      const next = statusSequence[currentStatusIndex + 1];
      return next;
    }, [statusSequence, currentStatusIndex]);

    // Check if current stop needs POD (REVERT to original logic)
    const currentStop = useMemo(() => {
      if (!currentStatus?.startsWith('at_stop_')) return null;
      const sequence = parseInt(currentStatus.split('_')[2]);
      return sortedStops.find(stop => stop.sequence === sequence);
    }, [currentStatus, sortedStops]);

    const needsPod = useMemo(() => {
      if (!currentStop) return false;
      return currentStop.stop_type === 'delivery' && 
             !load.documents?.some(doc => 
               doc.type === 'proof_of_delivery' && 
               doc.stop_sequence === currentStop.sequence
             );
    }, [currentStop, load.documents]);

    // Update the checkPodStatus function to be more targeted
    const checkPodStatus = async (stopSequence, shouldRetry = false) => {
        try {
            // If we're not in retry mode, just do a single check
            if (!shouldRetry) {
                const response = await api.get(`/api/loads/${load.id}/stops/${stopSequence}/pod-status`);
                return response.data.has_pod;
            }

            // Retry logic for post-upload verification
            const maxRetries = 3;
            let retryCount = 0;
            
            while (retryCount < maxRetries) {
                const response = await api.get(`/api/loads/${load.id}/stops/${stopSequence}/pod-status`);
                
                if (response.data.has_pod) {
                    return true;
                }
                
                retryCount++;
                if (retryCount < maxRetries) {
                    // Exponential backoff: 1s, 2s, 4s
                    await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount - 1)));
                }
            }
            
            return false;
        } catch (error) {
            console.error('Failed to check POD status:', error);
            return false;
        }
    };

    // Enhanced handleStatusUpdate for the new sliding system
    const handleStatusUpdate = async () => {
        try {
            setLoading(true);
            
            /* ----------------------------------------------------------
             *  If the next status is driver assignment and the load is
             *  missing either a driver or a vehicle, start wizard flow
             *  instead of rejecting the action.
             * -------------------------------------------------------- */
            if (nextStatus?.key === 'assigned_driver') {
                const hasDriver = load.assigned_driver_id || selectedDriver;
                const hasVehicle = load.assigned_vehicle_id;

                // Only start wizard if assignments are truly missing
                if (!hasDriver || !hasVehicle) {
                    // Prevent multiple wizard activations
                    if (wizardActive) {
                        console.warn('Wizard already active, ignoring duplicate activation');
                        setLoading(false);
                        return;
                    }

                    setWizardActive(true);
                    
                    // Start with driver selection if no driver, otherwise go to tracking source selection
                    if (!hasDriver) {
                        navigateToView(MODAL_VIEWS.DRIVER_SELECT);
                    } else {
                        // Driver already assigned, go directly to tracking source selection
                        // First check Samsara status
                        checkSamsaraStatus().then(connected => {
                            if (connected) {
                                fetchSamsaraVehicles();
                            }
                        });
                        navigateToView(MODAL_VIEWS.TRACKING_SOURCE_SELECT);
                    }
                    
                    setLoading(false);
                    return;
                }
            }

            // Ensure we're using the next status, not current status
            if (!nextStatus) {
                toast.error('No valid next status available');
                return;
            }

            /* ----------------------------------------------------------
             *  Check if we're transitioning away from a delivery stop
             *  and POD is required. If so, navigate to POD view instead
             *  of making the API call directly.
             * -------------------------------------------------------- */
            if (needsPod && currentStop?.stop_type === 'delivery') {
                navigateToView(MODAL_VIEWS.POD_SUBMIT);
                setLoading(false);
                return;
            }

            // Handle driver assignment
            if (nextStatus.key === 'assigned_driver') {
                if (!selectedDriver) {
                    toast.error('Please select a driver');
                    return;
                }
                
                const payload = {
                    status: nextStatus.key,
                    driver_id: selectedDriver
                };
                
                await api.put(`/api/carrier/loads/${load.id}/status`, payload);
                onClose();
                return;
            }

            // Build the payload with the next status
            const payload = {
                status: nextStatus.key
            };

            // Add additional data for transit or stop statuses
            if (nextStatus.key.startsWith('in_transit_')) {
                const [_, fromSeq, toSeq] = nextStatus.key.split('_');
                payload.from_stop_sequence = parseInt(fromSeq);
                payload.to_stop_sequence = parseInt(toSeq);
            } else if (nextStatus.key.startsWith('at_stop_')) {
                payload.sequence = parseInt(nextStatus.key.split('_')[2]);
            }

            // Make the API call
            await api.put(`/api/carrier/loads/${load.id}/status`, payload);
            onClose();

            // Show completion toast if needed
            if (nextStatus.key === 'completed') {
                toast.success(
                    <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        <div>
                            <p className="font-medium">Load Completed</p>
                            <p className="text-sm text-gray-600">
                                Load {getLoadDisplayId(load)} marked complete and moved to completed loads
                            </p>
                        </div>
                    </div>,
                    {
                        icon: false,
                        className: 'bg-white shadow-lg rounded-lg',
                    }
                );
            }
        } catch (error) {
            console.error('Status update error:', error);
            if (error.response?.data?.valid_transitions) {
                toast.error(`Invalid status transition. Valid transitions are: ${error.response.data.valid_transitions.join(', ')}`);
            } else {
                toast.error(error.response?.data?.error || 'Failed to update status');
            }
        } finally {
            setLoading(false);
        }
    };

    // Update handlePodSubmit to use retry mode
    const handlePodSubmit = async () => {
        try {
            setLoading(true);
            
            // Step 1: Upload POD
            if (podType === 'soft') {
                const formData = new FormData();
                formData.append('entity_type', 'load');
                formData.append('document_type', 'proof_of_delivery');
                formData.append('load_id', load.id);
                formData.append('stop_sequence', currentStop.sequence);
                formData.append('is_soft_pod', 'true');
                formData.append('signed_by', signedBy);
                formData.append('notes', podNotes);
                await api.post('/api/documents/upload', formData);
            } else {
                if (!podFiles.length) {
                    toast.error('Please select at least one file');
                    return;
                }

                const formData = new FormData();
                
                // Add all the required fields first
                formData.append('entity_type', 'load');
                formData.append('document_type', 'proof_of_delivery');
                formData.append('load_id', load.id);
                formData.append('stop_sequence', currentStop.sequence);
                formData.append('is_soft_pod', 'false');
                
                // Important: Append files with the expected 'files[]' key
                podFiles.forEach(file => {
                    formData.append('files[]', file);  // Changed to 'files[]' to match server expectation
                });

                // Add metadata after files
                formData.append('is_multi_page', podFiles.length > 1 ? 'true' : 'false');
                formData.append('page_count', podFiles.length.toString());

                await api.post('/api/documents/upload', formData);
            }

            // Step 2: Verify POD was registered (with retries)
            const podVerified = await checkPodStatus(currentStop.sequence, true);
            if (!podVerified) {
                throw new Error('POD verification failed after multiple attempts');
            }

            // Step 3: Update status
            const payload = {
                status: nextStatus.key,
                stop_sequence: currentStop.sequence
            };

            if (nextStatus.key.startsWith('in_transit_')) {
                payload.from_stop_id = nextStatus.fromStopId;
                payload.to_stop_id = nextStatus.toStopId;
                payload.from_stop_sequence = nextStatus.fromSequence;
                payload.to_stop_sequence = nextStatus.toSequence;
            }

            await api.put(`/api/carrier/loads/${load.id}/status`, payload);
            
            goBack();
            // Close modal after successful status update
            setTimeout(() => onClose(), 500);

            // Add success toast for completed loads
            if (nextStatus.key === 'completed') {
                toast.success(
                    <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        <div>
                            <p className="font-medium">Load Completed</p>
                            <p className="text-sm text-gray-600">
                                Load {getLoadDisplayId(load)} marked complete and moved to completed loads
                            </p>
                        </div>
                    </div>,
                    {
                        icon: false,
                        className: 'bg-white shadow-lg rounded-lg',
                    }
                );
            }
        } catch (error) {
            console.error('POD submission error:', error);
            toast.error(error.message || 'Failed to upload POD');
            return;
        } finally {
            setLoading(false);
        }
    };

    // Reset view state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            // Clean up all state when modal closes
            setCurrentView(MODAL_VIEWS.STATUS_UPDATE);
            setSlideDirection(0);
            setSelectedDriver('');
            setSelectedVehicle(null);
            setSelectedTrailer(null);
            setSelectedTrackingSource(load?.tracking_source || 'driver_app');
            setSelectedSamsaraVehicle(null);
            setPodType(null);
            setPodFiles([]);
            setSignedBy('');
            setPodNotes('');
            setWizardActive(false);
            setLoading(false);
        } else {
            // Reset to initial state when modal opens - use load's current tracking source
            setCurrentView(MODAL_VIEWS.STATUS_UPDATE);
            setSlideDirection(0);
            setSelectedDriver('');
            setSelectedVehicle(null);
            setSelectedTrailer(null);
            setSelectedTrackingSource(load?.tracking_source || 'driver_app');
            setSelectedSamsaraVehicle(null);
            setWizardActive(false);
            setLoading(false);
        }
    }, [isOpen, load?.tracking_source]);

    // Helper function to fetch updated load data after assignments
    const fetchUpdatedLoad = async () => {
        try {
            const response = await api.get(`/api/loads/${load.id}`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch updated load data:', error);
            return null;
        }
    };

    // Helper function to update Redux store based on user type
    const updateLoadInStore = (updatedLoad) => {
        // Determine if this is a carrier view based on the API endpoint pattern
        const isCarrierView = window.location.pathname.includes('carrier') || 
                              load.assigned_to_carrier !== undefined;
        
        if (isCarrierView) {
            dispatch(updateCarrierLoad({ id: updatedLoad.id, data: updatedLoad }));
        } else {
            dispatch(updateLoad({ id: updatedLoad.id, data: updatedLoad }));
        }
    };

    // Enhanced handlers for the new sliding view system
    const handleDriverSelect = async (driverId) => {
        try {
            setLoading(true);
            const response = await api.post(`/api/loads/${load.id}/reassign-driver`, {
                driver_id: driverId
            });

            if (response.data) {
                toast.success(`Driver assigned to load #${getLoadDisplayId(load)}`);
                
                const updatedLoad = await fetchUpdatedLoad();
                if (updatedLoad) {
                    updateLoadInStore(updatedLoad);
                    if (onStatusUpdate) {
                        onStatusUpdate(updatedLoad);
                    }
                    window.dispatchEvent(new CustomEvent('driverUpdated', { 
                        detail: { loadId: load.id, driverId } 
                    }));
                }
                
                // Return to main view
                goBack();
            }
        } catch (error) {
            toast.error('Failed to assign driver');
            console.error('Error assigning driver:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleVehicleSelect = async (vehicleId) => {
        try {
            setLoading(true);
            const response = await api.post(`/api/loads/${load.id}/assign-vehicle`, {
                vehicle_id: vehicleId
            });

            if (response.data) {
                toast.success(`Vehicle assigned to load #${getLoadDisplayId(load)}`);
                
                const updatedLoad = await fetchUpdatedLoad();
                if (updatedLoad) {
                    updateLoadInStore(updatedLoad);
                    if (onStatusUpdate) {
                        onStatusUpdate(updatedLoad);
                    }
                    window.dispatchEvent(new CustomEvent('vehicleUpdated', { 
                        detail: { loadId: load.id, vehicleId } 
                    }));
                }
                
                goBack();
            }
        } catch (error) {
            toast.error('Failed to assign vehicle');
            console.error('Error assigning vehicle:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleVehicleWithTrailerSelect = async (vehicle) => {
        setSelectedVehicle(vehicle);
        await fetchAvailableTrailers(vehicle.id);
        navigateToView(MODAL_VIEWS.TRAILER_SELECT);
    };

    const handleTrailerSelect = async (trailerId) => {
        try {
            setLoading(true);
            const response = await api.post(`/api/loads/${load.id}/assign-vehicle`, {
                vehicle_id: selectedVehicle.id,
                trailer_id: trailerId
            });

            if (response.data) {
                toast.success(`Vehicle and trailer assigned to load #${getLoadDisplayId(load)}`);
                
                const updatedLoad = await fetchUpdatedLoad();
                if (updatedLoad) {
                    updateLoadInStore(updatedLoad);
                    if (onStatusUpdate) {
                        onStatusUpdate(updatedLoad);
                    }
                    window.dispatchEvent(new CustomEvent('vehicleUpdated', { 
                        detail: { loadId: load.id, vehicleId: selectedVehicle.id } 
                    }));
                    window.dispatchEvent(new CustomEvent('trailerUpdated', { 
                        detail: { loadId: load.id, trailerId } 
                    }));
                }
                
                goBack();
            }
        } catch (error) {
            toast.error('Failed to assign vehicle and trailer');
            console.error('Error assigning vehicle and trailer:', error);
        } finally {
            setLoading(false);
        }
    };

    // AFTER handleTrailerSelect, add new helper to initiate trailer assignment for the currently assigned vehicle
    const handleAssignTrailerClick = async () => {
        if (!load.assigned_vehicle_id) return;
        // Try to locate the current vehicle from the already-fetched list
        let currentVehicle = availableVehicles.find(v => v.id === load.assigned_vehicle_id);
        if (!currentVehicle) {
            // Fallback – create a minimal stub so the trailer view still has a vehicle reference
            currentVehicle = { id: load.assigned_vehicle_id };
        }
        setSelectedVehicle(currentVehicle);
        await fetchAvailableTrailers(currentVehicle.id);
        navigateToView(MODAL_VIEWS.TRAILER_SELECT);
    };

    // Helper functions for requirement checking
    const canReassignDriver = () => {
        const status = load.status || '';
        return status === 'assigned_driver' ||
               status === 'trip_started' ||
               status.startsWith('at_stop_') ||
               status.startsWith('in_transit_');
    };

    const canAssignVehicle = () => {
        const status = load.status || '';
        return ['assigned', 'assigned_driver', 'trip_started'].includes(status) ||
               status.startsWith('at_stop_') ||
               status.startsWith('in_transit_');
    };

    // NEW: determine if a trailer can be (re)assigned – requires a vehicle but no trailer, or allow change when trailer already exists
    const canAssignTrailer = () => {
        return Boolean(load.assigned_vehicle_id);
    };

    // Check requirements for status progression
    const getStatusRequirements = () => {
        // Check if load is using Samsara ELD tracking
        const hasSamsaraTracking = (
            load.tracking_source === 'samsara' && load.samsara_vehicle_id
        );
        
        if (nextStatus?.key === 'assigned_driver') {
            // For assigned_driver, accept either platform vehicle OR Samsara tracking
            const hasVehicle = load.assigned_vehicle_id || hasSamsaraTracking;
            return {
                type: 'driver_assignment',
                canProceed: load.assigned_driver_id && hasVehicle
            };
        }
        
        // Trailer requirement for pickup stops - skip if using Samsara tracking
        if (nextStatus?.key?.startsWith('at_stop_') && 
            sortedStops.find(s => s.sequence === parseInt(nextStatus.key.split('_')[2]))?.stop_type === 'pickup') {
            // Samsara-tracked loads don't need platform trailer assignment
            if (hasSamsaraTracking) {
                return null; // No requirement - Samsara handles trailer tracking
            }
            return {
                type: 'trailer_requirement',
                canProceed: load.assigned_trailer_id
            };
        }
        
        return null;
    };

    // Mobile-optimized main status view
    const renderMainStatusView = () => {
        const requirements = getStatusRequirements();

        // Prepare action buttons
        const actionButtons = [];
        
        if (canReassignDriver()) {
            actionButtons.push(
                <button
                    key="driver"
                    onClick={() => navigateToView(MODAL_VIEWS.DRIVER_SELECT)}
                    disabled={loading}
                    className="px-3 py-2 md:px-3 md:py-1.5 text-sm rounded-xl md:rounded-lg inline-flex items-center gap-2 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors duration-200 touch-manipulation"
                >
                    <UserPlus className="w-4 h-4" />
                    <span className="hidden sm:inline">{load.assigned_driver_id ? 'Change Driver' : 'Assign Driver'}</span>
                    <span className="sm:hidden">Driver</span>
                </button>
            );
        }
        
        if (canAssignVehicle()) {
            // Check if load has any vehicle/tracking configured
            const hasVehicleOrTracking = load.assigned_vehicle_id || 
                (load.tracking_source === 'samsara' && load.samsara_vehicle_id);
            
            actionButtons.push(
                <button
                    key="vehicle"
                    onClick={async () => {
                        // Check Samsara status and fetch vehicles if connected
                        const connected = await checkSamsaraStatus();
                        if (connected) {
                            await fetchSamsaraVehicles();
                        }
                        // Go to tracking source selection to choose between platform vehicle or Samsara
                        navigateToView(MODAL_VIEWS.TRACKING_SOURCE_SELECT);
                    }}
                    disabled={loading}
                    className="px-3 py-2 md:px-3 md:py-1.5 text-sm rounded-xl md:rounded-lg inline-flex items-center gap-2 bg-green-50 text-green-600 hover:bg-green-100 transition-colors duration-200 touch-manipulation"
                >
                    <Truck className="w-4 h-4" />
                    <span className="hidden sm:inline">{hasVehicleOrTracking ? 'Change Tracking' : 'Assign Vehicle'}</span>
                    <span className="sm:hidden">Vehicle</span>
                </button>
            );
        }

        // Only show trailer button for platform vehicle tracking (not Samsara)
        const isSamsaraTracked = load.tracking_source === 'samsara' && load.samsara_vehicle_id;
        if (canAssignTrailer() && !isSamsaraTracked) {
            actionButtons.push(
                <button
                    key="trailer"
                    onClick={handleAssignTrailerClick}
                    disabled={loading || !load.assigned_vehicle_id}
                    className="px-3 py-2 md:px-3 md:py-1.5 text-sm rounded-xl md:rounded-lg inline-flex items-center gap-2 bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors duration-200 touch-manipulation"
                >
                    <Package className="w-4 h-4" />
                    <span className="hidden sm:inline">{load.assigned_trailer_id ? 'Change Trailer' : 'Assign Trailer'}</span>
                    <span className="sm:hidden">Trailer</span>
                </button>
            );
        }

        // Add exception button if trip is in progress (after trip_started, before completed/cancelled)
        const canReportException = () => {
            const status = (load.status || '').toLowerCase();
            return status === 'trip_started' || 
                   status.startsWith('at_stop_') || 
                   status.startsWith('in_transit_');
        };

        if (canReportException()) {
            actionButtons.push(
                <button
                    key="exception"
                    onClick={() => navigateToView(MODAL_VIEWS.EXCEPTION_REPORT)}
                    disabled={loading}
                    className="px-3 py-2 md:px-3 md:py-1.5 text-sm rounded-xl md:rounded-lg inline-flex items-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 transition-colors duration-200 touch-manipulation"
                >
                    <AlertTriangle className="w-4 h-4" />
                    <span className="hidden sm:inline">Report Exception</span>
                    <span className="sm:hidden">Exception</span>
                </button>
            );
        }

        return (
            <div className="h-full flex flex-col">
                <MobileHeader 
                    title="Update Load Status"
                    subtitle={`Load ${getLoadDisplayId(load)}`}
                    onClose={onClose}
                    actions={actionButtons}
                />

                {/* Requirements Alert */}
                {requirements && !requirements.canProceed && (
                    <div className="px-4 pt-4 md:px-6">
                        <RequirementAlert 
                            type={requirements.type} 
                            currentStatus={currentStatus} 
                            load={load} 
                        />
                    </div>
                )}

                {/* Timeline scroll area */}
                <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6 scroll-smooth scrollbar-thin scrollbar-thumb-gray-200 hover:scrollbar-thumb-gray-300">
                    <div className="space-y-0">
                        {statusSequence.map((status, index) => (
                            <StatusTimelineItem
                                key={status.key}
                                status={status}
                                isCompleted={index <= currentStatusIndex}
                                isNext={index === currentStatusIndex + 1}
                                selectedDriver={selectedDriver}
                                setSelectedDriver={setSelectedDriver}
                                availableDrivers={availableDrivers}
                            />
                        ))}
                    </div>
                </div>

                                 {/* Sticky Footer with safe area padding */}
                 <div className="mt-auto border-t border-gray-100 bg-white">
                     <div className="px-4 py-4 pb-safe md:px-6 md:pb-4">
                         <div className="flex flex-col md:flex-row gap-3 md:justify-between md:items-center">
                             <button
                                 onClick={onClose}
                                 className="order-2 md:order-1 px-6 py-4 md:px-4 md:py-2 text-gray-600 hover:text-gray-800 transition-colors touch-manipulation rounded-2xl md:rounded-lg hover:bg-gray-50"
                             >
                                 Cancel
                             </button>
                             {nextStatus && (
                                 <button
                                     onClick={handleStatusUpdate}
                                     disabled={loading || (
                                         nextStatus.key !== 'assigned_driver' && nextStatus.requiresDriver && !selectedDriver
                                     ) || (
                                         nextStatus.key !== 'assigned_driver' && requirements && !requirements.canProceed
                                     )}
                                     className={`
                                         order-1 md:order-2 px-8 py-4 md:px-4 md:py-2 rounded-2xl md:rounded-lg font-semibold text-base md:font-medium md:text-sm
                                         inline-flex items-center justify-center transition-all duration-200 touch-manipulation
                                         ${loading 
                                             ? 'bg-blue-100 text-blue-600' 
                                             : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl active:scale-98'
                                         }
                                         disabled:opacity-50 disabled:cursor-not-allowed
                                     `}
                                 >
                                     {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                     {loading ? 'Updating...' : `Mark as ${nextStatus.label}`}
                                 </button>
                             )}
                         </div>
                     </div>
                 </div>
            </div>
        );
    };

    // Render current view content
    const renderCurrentView = () => {        
        switch (currentView) {
            case MODAL_VIEWS.DRIVER_SELECT:
                return (
                    <DriverSelectView
                        availableDrivers={availableDrivers}
                        selectedDriver={selectedDriver}
                        setSelectedDriver={setSelectedDriver}
                        onSelect={wizardActive ? handleWizardDriverSelect : handleDriverSelect}
                        onBack={goBack}
                        onClose={onClose}
                        loading={loading}
                        currentDriverId={load.assigned_driver_id}
                    />
                );
                
            case MODAL_VIEWS.TRACKING_SOURCE_SELECT:
                return (
                    <TrackingSourceSelectView
                        samsaraConnected={samsaraConnected}
                        selectedTrackingSource={selectedTrackingSource}
                        onSelect={wizardActive ? handleWizardTrackingSourceSelect : handleTrackingSourceSelect}
                        onBack={goBack}
                        onClose={onClose}
                        loading={loading}
                    />
                );
                
            case MODAL_VIEWS.SAMSARA_VEHICLE_SELECT:
                return (
                    <SamsaraVehicleSelectView
                        vehicles={samsaraVehicles}
                        selectedVehicle={selectedSamsaraVehicle}
                        onSelect={wizardActive ? handleWizardSamsaraVehicleSelect : handleSamsaraVehicleSelect}
                        onBack={() => navigateToView(MODAL_VIEWS.TRACKING_SOURCE_SELECT, -1)}
                        onClose={onClose}
                        loading={loading}
                        loadingSamsaraVehicles={loadingSamsaraVehicles}
                    />
                );
                
            case MODAL_VIEWS.VEHICLE_SELECT:
                return (
                    <VehicleSelectView
                        vehicles={availableVehicles}
                        selectedVehicle={selectedVehicle}
                        setSelectedVehicle={setSelectedVehicle}
                        onSelect={wizardActive ? handleWizardVehicleSelect : handleVehicleSelect}
                        onSelectWithTrailer={wizardActive ? handleWizardVehicleWithTrailerSelect : handleVehicleWithTrailerSelect}
                        onBack={wizardActive ? () => navigateToView(MODAL_VIEWS.TRACKING_SOURCE_SELECT, -1) : goBack}
                        onClose={onClose}
                        loading={loading}
                        currentVehicleId={load.assigned_vehicle_id}
                    />
                );
                
            case MODAL_VIEWS.TRAILER_SELECT:
                return (
                    <TrailerSelectView
                        vehicleId={selectedVehicle?.id}
                        trailers={availableTrailers}
                        selectedTrailer={selectedTrailer}
                        setSelectedTrailer={setSelectedTrailer}
                        onSelect={(trailerId, trailerObj) => {
                            if (wizardActive) {
                                setSelectedTrailer(trailerObj);
                                handleWizardTrailerSelect(trailerId);
                            } else {
                                handleTrailerSelect(trailerId);
                            }
                        }}
                        onBack={goBack}
                        onClose={onClose}
                        loading={loading}
                        currentTrailerId={load.assigned_trailer_id}
                    />
                );
                
            case MODAL_VIEWS.POD_SUBMIT:
                return (
                    <PodView
                        podType={podType}
                        setPodType={setPodType}
                        signedBy={signedBy}
                        setSignedBy={setSignedBy}
                        podNotes={podNotes}
                        setPodNotes={setPodNotes}
                        podFiles={podFiles}
                        setPodFiles={setPodFiles}
                        loading={loading}
                        handlePodSubmit={handlePodSubmit}
                        onBack={goBack}
                        onClose={onClose}
                    />
                );
            
            case MODAL_VIEWS.EXCEPTION_REPORT:
                return (
                    <ExceptionReportView
                        load={load}
                        onBack={goBack}
                        onClose={onClose}
                        loading={loading}
                        setLoading={setLoading}
                        dispatch={dispatch}
                    />
                );
                
            default:
                return renderMainStatusView();
        }
    };

    /* ------------------------------------------------------------------
     *  Wizard-specific helpers
     * ------------------------------------------------------------------ */

    // Reset wizard state helper
    const resetWizardState = () => {
        setWizardActive(false);
        setSelectedDriver('');
        setSelectedVehicle(null);
        setSelectedTrailer(null);
        setSelectedTrackingSource(load?.tracking_source || 'driver_app');
        setSelectedSamsaraVehicle(null);
        setLoading(false);
    };

    // Retry status update with exponential backoff for race conditions
    const retryStatusUpdate = async (payload, maxRetries = 3) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                console.log(`Wizard: Status update attempt ${attempt}/${maxRetries}`, payload);
                const response = await api.put(`/api/carrier/loads/${load.id}/status`, payload);
                return response;
            } catch (error) {
                const isVehicleRequiredError = error.response?.data?.code === 'VEHICLE_REQUIRED';
                const isInvalidTransition = Boolean(
                    error.response?.data?.valid_transitions ||
                    error.response?.data?.code === 'INVALID_TRANSITION' ||
                    error.response?.status === 400 ||
                    error.response?.status === 409
                );
                const isLastAttempt = attempt === maxRetries;
                
                console.log(`Wizard: Status update attempt ${attempt} failed`, {
                    error: error.response?.data?.error,
                    code: error.response?.data?.code,
                    isVehicleRequiredError,
                    isInvalidTransition,
                    isLastAttempt
                });
                
                // If it's a vehicle requirement error and not the last attempt, retry after delay
                if (isVehicleRequiredError && !isLastAttempt) {
                    const delayMs = Math.pow(2, attempt - 1) * 1000; // 1s, 2s, 4s
                    console.log(`Wizard: Retrying after ${delayMs}ms delay...`);
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                    
                    // Verify vehicle assignment before retrying
                    const verifyLoad = await fetchUpdatedLoad();
                    if (!verifyLoad?.assigned_vehicle_id) {
                        throw new Error('Vehicle assignment verification failed during retry');
                    }
                    continue;
                }

                // If the server reported an invalid transition, check if the load has already progressed.
                // If so, treat this as success to avoid spurious error toasts.
                if (isInvalidTransition) {
                    try {
                        const verifyLoad = await fetchUpdatedLoad();
                        if (verifyLoad && verifyLoad.status && (verifyLoad.status || '').toLowerCase() !== 'assigned') {
                            console.log('Wizard: Detected progressed status after invalid transition, treating as success', {
                                status: verifyLoad.status
                            });
                            return { data: verifyLoad };
                        }
                    } catch (verifyError) {
                        // Fall through to throw original error if verification fails
                    }
                }
                
                // If it's not a vehicle requirement error or it's the last attempt, throw the error
                throw error;
            }
        }
    };

    const handleWizardDriverSelect = async (driverId) => {
        console.log('handleWizardDriverSelect: Starting with driverId', driverId);
        setSelectedDriver(driverId);
        
        // Check Samsara status and fetch vehicles if connected
        console.log('handleWizardDriverSelect: Checking Samsara status...');
        const connected = await checkSamsaraStatus();
        console.log('handleWizardDriverSelect: Samsara connected =', connected);
        
        if (connected) {
          console.log('handleWizardDriverSelect: Fetching Samsara vehicles...');
          await fetchSamsaraVehicles();
        }
        
        // Navigate to tracking source selection
        console.log('handleWizardDriverSelect: Navigating to TRACKING_SOURCE_SELECT');
        navigateToView(MODAL_VIEWS.TRACKING_SOURCE_SELECT);
    };

    // Handle tracking source selection in wizard
    const handleWizardTrackingSourceSelect = (source) => {
        setSelectedTrackingSource(source);
        
        if (source === 'samsara') {
          // Go to Samsara vehicle selection
          navigateToView(MODAL_VIEWS.SAMSARA_VEHICLE_SELECT);
        } else {
          // Go to platform vehicle selection (existing flow)
          navigateToView(MODAL_VIEWS.VEHICLE_SELECT);
        }
    };
    
    // Handle tracking source selection in non-wizard mode (from action button)
    const handleTrackingSourceSelect = (source) => {
        setSelectedTrackingSource(source);
        
        if (source === 'samsara') {
          // Go to Samsara vehicle selection
          navigateToView(MODAL_VIEWS.SAMSARA_VEHICLE_SELECT);
        } else {
          // Go to platform vehicle selection
          navigateToView(MODAL_VIEWS.VEHICLE_SELECT);
        }
    };

    // Handle Samsara vehicle selection in wizard
    const handleWizardSamsaraVehicleSelect = async (vehicle) => {
        setSelectedSamsaraVehicle(vehicle);
        
        // Finalize with Samsara tracking
        await finalizeWizardWithSamsara(vehicle);
    };
    
    // Handle Samsara vehicle selection in non-wizard mode (just changing tracking)
    const handleSamsaraVehicleSelect = async (vehicle) => {
        setSelectedSamsaraVehicle(vehicle);
        
        try {
            setLoading(true);
            
            console.log('Updating tracking to Samsara vehicle:', {
                samsara_vehicle_id: vehicle.id,
                samsara_vehicle_name: vehicle.name,
                loadId: load.id
            });
            
            // Update tracking source to Samsara
            await loadTrackingApi.updateTrackingSource(load.id, {
                tracking_source: 'samsara',
                samsara_vehicle_id: vehicle.id,
                samsara_vehicle_name: vehicle.name
            });
            
            // Fetch updated load data
            const updatedLoad = await fetchUpdatedLoad();
            if (updatedLoad) {
                updateLoadInStore(updatedLoad);
                if (onStatusUpdate) {
                    onStatusUpdate(updatedLoad);
                }
            }
            
            toast.success(
                <div className="flex items-center gap-2">
                    <Radio className="w-5 h-5" />
                    <div>
                        <p className="font-medium">Samsara Tracking Configured</p>
                        <p className="text-sm text-gray-600">
                            Vehicle: {vehicle.name}
                        </p>
                    </div>
                </div>,
                { icon: false, className: 'bg-white shadow-lg rounded-lg' }
            );
            
            onClose();
        } catch (error) {
            console.error('Error updating tracking source:', error);
            toast.error('Failed to update tracking source');
        } finally {
            setLoading(false);
        }
    };

    const finalizeWizard = async () => {
        try {
            // Determine which driver to use: prefer wizard selection, otherwise existing assigned driver
            const driverIdToUse = selectedDriver || load.assigned_driver_id;
            if (!driverIdToUse) {
                throw new Error('No driver selected for assignment');
            }

            console.log('Wizard: Starting finalize with payload', {
                status: 'assigned_driver',
                driver_id: driverIdToUse,
                loadId: load.id
            });

            const payload = {
                status: 'assigned_driver',
                driver_id: driverIdToUse
            };

            // Update status with driver assignment (with retry logic for race conditions)
            const statusResponse = await retryStatusUpdate(payload);
            console.log('Wizard: Status update successful', statusResponse.data);

            // Fetch updated load data
            const updatedLoad = await fetchUpdatedLoad();
            if (updatedLoad) {
                updateLoadInStore(updatedLoad);
                if (onStatusUpdate) {
                    onStatusUpdate(updatedLoad);
                }
            }

            // Build comprehensive success message based on what was assigned
            const assignments = [];
            
            // Find the selected driver's name
            const driverName = availableDrivers.find(d => String(d.id) === String(driverIdToUse))?.name || 'Driver';
            assignments.push(driverName);
            
            // Check if vehicle was assigned during wizard
            if (selectedVehicle) {
                const vehicleDesc = selectedVehicle.year ? 
                    `${selectedVehicle.year} ${selectedVehicle.type || 'Vehicle'}` : 
                    (selectedVehicle.type || 'Vehicle');
                assignments.push(vehicleDesc);
            }
            
            // Check if trailer was assigned during wizard
            if (selectedTrailer) {
                const trailerDesc = selectedTrailer.trailer_number ? 
                    `Trailer ${selectedTrailer.trailer_number}` : 
                    'Trailer';
                assignments.push(trailerDesc);
            }

            // Success notification with comprehensive message
            toast.success(
                <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <div>
                        <p className="font-medium">Successfully Updated</p>
                        <p className="text-sm text-gray-600">
                            {assignments.length > 1 
                                ? `Assigned ${assignments.join(', ')} to Load #${getLoadDisplayId(load)}`
                                : `${assignments[0]} assigned to Load #${getLoadDisplayId(load)}`
                            }
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            Status: Driver Assigned
                        </p>
                    </div>
                </div>,
                { icon: false, className: 'bg-white shadow-lg rounded-lg' }
            );

            // Clean up and close
            resetWizardState();
            onClose();
        } catch (error) {
            console.error('Finalize wizard error:', error);
            
            // Handle specific API error responses
            if (error.response?.data) {
                const errorData = error.response.data;
                console.error('Wizard: API error details', {
                    error: errorData.error,
                    code: errorData.code,
                    validTransitions: errorData.valid_transitions,
                    missingRequirements: errorData.missing_requirements
                });
                
                // Provide more specific error messages
                let errorMessage = errorData.error || 'Failed to assign driver and update status';
                
                if (errorData.code === 'VEHICLE_REQUIRED') {
                    errorMessage = 'Vehicle assignment is required before assigning driver. Please ensure vehicle assignment completed successfully.';
                } else if (errorData.code === 'DRIVER_REQUIRED') {
                    errorMessage = 'Driver assignment failed. Please try again.';
                } else if (errorData.missing_requirements) {
                    errorMessage = `Missing requirements: ${errorData.missing_requirements.join(', ')}`;
                }
                
                toast.error(errorMessage);
            } else {
                const errorMessage = error.message || 'Failed to assign driver and update status';
                toast.error(errorMessage);
            }
            
            // Reset wizard state to allow retry
            resetWizardState();
            // Navigate back to main view to allow user to retry
            setCurrentView(MODAL_VIEWS.STATUS_UPDATE);
        }
    };

    // Finalize wizard with Samsara tracking (no platform vehicle needed)
    const finalizeWizardWithSamsara = async (samsaraVehicle) => {
        try {
            setLoading(true);
            
            const driverIdToUse = selectedDriver || load.assigned_driver_id;
            if (!driverIdToUse) {
                throw new Error('No driver selected for assignment');
            }

            console.log('Wizard: Finalizing with Samsara tracking', {
                driver_id: driverIdToUse,
                samsara_vehicle_id: samsaraVehicle.id,
                samsara_vehicle_name: samsaraVehicle.name,
                loadId: load.id
            });

            // Step 1: Update tracking source to Samsara FIRST
            // This must happen before status update so backend knows about Samsara tracking
            console.log('Wizard: Setting Samsara tracking source...');
            await loadTrackingApi.updateTrackingSource(load.id, {
                tracking_source: 'samsara',
                samsara_vehicle_id: samsaraVehicle.id,
                samsara_vehicle_name: samsaraVehicle.name
            });
            console.log('Wizard: Samsara tracking source set successfully');

            // Step 2: Update status to assigned_driver with driver_id
            // The status update endpoint handles driver assignment internally
            // This prevents premature SMS notifications from reassign-driver endpoint
            console.log('Wizard: Updating status to assigned_driver with driver...');
            const statusPayload = {
                status: 'assigned_driver',
                driver_id: driverIdToUse
            };
            
            await retryStatusUpdate(statusPayload);
            console.log('Wizard: Status and driver assignment completed successfully');

            // Fetch updated load data
            const updatedLoad = await fetchUpdatedLoad();
            if (updatedLoad) {
                updateLoadInStore(updatedLoad);
                if (onStatusUpdate) {
                    onStatusUpdate(updatedLoad);
                }
            }

            // Build success message
            const driverName = availableDrivers.find(d => String(d.id) === String(driverIdToUse))?.name || 'Driver';

            toast.success(
                <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <div>
                        <p className="font-medium">Successfully Updated</p>
                        <p className="text-sm text-gray-600">
                            Assigned {driverName} to Load #{getLoadDisplayId(load)}
                        </p>
                        <p className="text-xs text-emerald-600 mt-1 flex items-center">
                            <Radio className="w-3 h-3 mr-1" />
                            Samsara ELD: {samsaraVehicle.name}
                        </p>
                    </div>
                </div>,
                { icon: false, className: 'bg-white shadow-lg rounded-lg' }
            );

            // Clean up and close
            resetWizardState();
            onClose();
        } catch (error) {
            console.error('Finalize wizard with Samsara error:', error);
            
            const errorMessage = error.response?.data?.error || error.message || 'Failed to complete setup with Samsara tracking';
            toast.error(errorMessage);
            
            // Reset wizard state to allow retry
            resetWizardState();
            setCurrentView(MODAL_VIEWS.STATUS_UPDATE);
        } finally {
            setLoading(false);
        }
    };

    const handleWizardVehicleSelect = async (vehicleId) => {
        try {
            setLoading(true);

            console.log('Wizard: Assigning vehicle only', {
                vehicleId: vehicleId,
                loadId: load.id
            });

            // Store the selected vehicle object for finalizeWizard to use
            const vehicle = availableVehicles.find(v => v.id === vehicleId);
            if (vehicle) {
                setSelectedVehicle(vehicle);
            }

            // Step 1: Assign vehicle
            const vehicleResponse = await api.post(`/api/loads/${load.id}/assign-vehicle`, {
                vehicle_id: vehicleId
            });

            if (!vehicleResponse.data) {
                throw new Error('Failed to assign vehicle - no response data');
            }

            console.log('Wizard: Vehicle assignment response', vehicleResponse.data);

            // NO TOAST HERE - let finalizeWizard handle all notifications

            // Step 2: Refresh load data to verify vehicle is assigned
            const updatedLoad = await fetchUpdatedLoad();
            if (updatedLoad) {
                updateLoadInStore(updatedLoad);
                
                // Verify vehicle was actually assigned
                if (!updatedLoad.assigned_vehicle_id) {
                    console.error('Vehicle assignment failed - not reflected in updated load data');
                    throw new Error('Vehicle assignment was not saved properly');
                }
                
                console.log('Wizard: Load updated with vehicle assignment', {
                    vehicleId: updatedLoad.assigned_vehicle_id
                });
            }

            // Step 3: If a driver is already selected or assigned, finalize; otherwise, continue to driver selection
            const hasDriverForFinalize = Boolean(selectedDriver) || Boolean(updatedLoad?.assigned_driver_id);
            if (hasDriverForFinalize) {
                await finalizeWizard();
            } else {
                // Move to driver selection to complete the wizard
                navigateToView(MODAL_VIEWS.DRIVER_SELECT);
            }

        } catch (error) {
            console.error('Wizard vehicle assign error:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to assign vehicle';
            toast.error(errorMessage);
            
            // Reset state and go back to main view for retry
            resetWizardState();
            setCurrentView(MODAL_VIEWS.STATUS_UPDATE);
        } finally {
            setLoading(false);
        }
    };

    const handleWizardVehicleWithTrailerSelect = async (vehicle) => {
        try {
            setSelectedVehicle(vehicle);
            await fetchAvailableTrailers(vehicle.id);
            navigateToView(MODAL_VIEWS.TRAILER_SELECT);
        } catch (error) {
            console.error('Error fetching trailers:', error);
            toast.error('Failed to load available trailers');
            resetWizardState();
            setCurrentView(MODAL_VIEWS.STATUS_UPDATE);
        }
    };

    const handleWizardTrailerSelect = async (trailerId) => {
        try {
            setLoading(true);

            if (!selectedVehicle) {
                throw new Error('No vehicle selected for trailer assignment');
            }

            console.log('Wizard: Starting vehicle and trailer assignment sequence', {
                vehicleId: selectedVehicle.id,
                trailerId: trailerId,
                loadId: load.id
            });

            // Step 1: First assign the vehicle
            console.log('Wizard: Step 1 - Assigning vehicle');
            const vehicleResponse = await api.post(`/api/loads/${load.id}/assign-vehicle`, {
                vehicle_id: selectedVehicle.id
            });

            if (!vehicleResponse.data) {
                throw new Error('Failed to assign vehicle - no response data');
            }

            console.log('Wizard: Vehicle assignment successful', vehicleResponse.data);

            // Step 2: Then assign the trailer separately 
            console.log('Wizard: Step 2 - Assigning trailer');
            const trailerResponse = await api.post(`/api/loads/${load.id}/assign-trailer`, {
                trailer_id: trailerId
            });

            if (!trailerResponse.data) {
                throw new Error('Failed to assign trailer - no response data');
            }

            console.log('Wizard: Trailer assignment successful', trailerResponse.data);

            // NO TOAST HERE - let finalizeWizard handle all notifications

            // Step 3: Refresh load data to verify both assignments are in place
            const updatedLoad = await fetchUpdatedLoad();
            if (updatedLoad) {
                updateLoadInStore(updatedLoad);
                
                // Verify both assignments were saved
                if (!updatedLoad.assigned_vehicle_id) {
                    console.error('Vehicle assignment failed - not reflected in updated load data');
                    throw new Error('Vehicle assignment was not saved properly');
                }
                
                if (!updatedLoad.assigned_trailer_id) {
                    console.error('Trailer assignment failed - not reflected in updated load data');
                    throw new Error('Trailer assignment was not saved properly');
                }
                
                console.log('Wizard: Load updated with both assignments', {
                    vehicleId: updatedLoad.assigned_vehicle_id,
                    trailerId: updatedLoad.assigned_trailer_id
                });
            }

            // Step 4: If a driver is already selected or assigned, finalize; otherwise, continue to driver selection
            const hasDriverForFinalize = Boolean(selectedDriver) || Boolean(updatedLoad?.assigned_driver_id);
            if (hasDriverForFinalize) {
                await finalizeWizard();
            } else {
                navigateToView(MODAL_VIEWS.DRIVER_SELECT);
            }

        } catch (error) {
            console.error('Wizard trailer assign error:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to assign vehicle and trailer';
            toast.error(errorMessage);
            
            // Reset state and go back to main view for retry
            resetWizardState();
            setCurrentView(MODAL_VIEWS.STATUS_UPDATE);
        } finally {
            setLoading(false);
        }
    };

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 overflow-hidden bg-black/50 backdrop-blur-sm"
                    onClick={(e) => e.target === e.currentTarget && onClose()}
                >
                    {/* Mobile: Full screen, Desktop: Centered modal */}
                    <div className={`
                        flex items-center justify-center
                        ${isMobile ? 'h-full w-full' : 'min-h-screen p-4'}
                    `}>
                        <motion.div 
                            className={`
                                relative bg-white shadow-xl overflow-hidden flex flex-col
                                ${isMobile 
                                    ? 'w-full h-full max-h-screen rounded-none' 
                                    : 'rounded-2xl max-w-4xl w-full h-[85vh]'
                                }
                            `}
                            {...(isMobile ? onPan : {})}
                        >
                            <AnimatePresence mode="wait" custom={slideDirection}>
                                <motion.div
                                    key={currentView}
                                    custom={slideDirection}
                                    variants={slideVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{
                                        x: { type: "spring", stiffness: 300, damping: 30 },
                                        opacity: { duration: 0.2 }
                                    }}
                                    className="h-full flex flex-col"
                                >
                                    {renderCurrentView()}
                                </motion.div>
                            </AnimatePresence>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
  }
);

// Add display name for debugging purposes
StatusUpdateModal.displayName = 'StatusUpdateModal';

export default StatusUpdateModal; 